import { isEmpty, isNil } from 'lodash';
import React from 'react';
import { View } from '@shared/components/View';
import {
  FilterOption,
  FilterSelection,
  FilterSelectionState,
  FilterTagOption,
  FilterTagSelection,
} from '@shared/components/filter/types';
import { Pill } from '@shared/components/pill';
import { MBTextBody1 } from '@shared/components/typography';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { Colors } from '@shared/styles';
import { i18n } from '@shared/util/i18n';

export function RenderItem({ data, negate }: { data: FilterTagOption; negate?: boolean }) {
  return (
    <Pill
      backgroundColor={data.archived ? undefined : data.backgroundColor}
      round
      sections={[data.label]}
      maxWidth={215}
      italic={data.archived}
      strikeThrough={negate}
    />
  );
}

export function MobileTagSelections({
  filter,
  selectedOptions,
}: {
  filter: FilterOption;
  selectedOptions: Map<string, FilterSelectionState>;
}) {
  const rawSelections: FilterSelection | undefined = !isNil(filter.uuid)
    ? selectedOptions.get(filter.uuid)?.selection
    : undefined;

  const isFilterTagSelectionArray = (
    selections: FilterSelection,
  ): selections is FilterTagSelection[] =>
    Array.isArray(selections) && selections.every(s => typeof s === 'object' && 'value' in s);

  const tagSelections: FilterTagSelection[] =
    rawSelections && isFilterTagSelectionArray(rawSelections) ? rawSelections : [];

  if (isNil(filter.component) || !isTagComponent(filter.component)) {
    return null;
  }

  if (isEmpty(tagSelections)) {
    return (
      <View>
        <MBTextBody1 color={Colors.v2.gray50}>{i18n('common.selectOptions')}</MBTextBody1>
      </View>
    );
  }

  const tagComponent = filter.component;
  const emptyLabel = i18n('card.actions.filters.emptyLabel', { label: filter.label ?? '' });

  const availableTagOptions: FilterTagOption[] = (tagComponent.options ?? []).map(o => ({
    value: o.value,
    label: o.label ?? '',
    backgroundColor: o.backgroundColor as FilterTagOption['backgroundColor'],
    archived: o.archived ?? undefined,
  }));

  return (
    <View row wrap>
      {tagSelections.map(selection => (
        <RenderItem
          key={selection.value}
          data={toFilterTagOption(selection, availableTagOptions, emptyLabel)}
        />
      ))}
    </View>
  );
}

// --- Helper functions ------------------------------------------------------

// Basic shape we care about from whatever ends up in `filter.selection`.
type RawSelection = {
  value: 'empty' | string | number;
  label?: string;
  backgroundColor?: FilterTagOption['backgroundColor'];
  archived?: boolean;
};

/**
 * Convert a raw selection value coming from filter state into a FilterTagOption that the
 * MobileMultiSelect component can render.
 */
export function toFilterTagOption(
  selection: RawSelection,
  availableOptions: FilterTagOption[],
  emptyLabel: string,
): FilterTagOption {
  // Handle the special "empty" placeholder option first.
  if (selection.value === 'empty') {
    return { value: 'empty', label: emptyLabel };
  }

  // Attempt to find a matching canonical option from the component's configuration.
  const match = availableOptions.find(
    option => typeof selection.value === 'string' && option.value === selection.value,
  );

  if (match) {
    // Ensure the label is never undefined so the UI always has text to display.
    return { ...match, label: match.label ?? '' };
  }

  // Fallback for archived or otherwise unknown tags that may still be selected.
  const backgroundColor = selection.backgroundColor ?? undefined;

  return {
    value: selection.value,
    label: selection.label ?? '',
    backgroundColor: backgroundColor as FilterTagOption['backgroundColor'],
    archived: selection.archived,
  };
}
